/**
 * FILE: apps/admin/src/lib/useSetupStatus.ts
 * PURPOSE: Single-source-of-truth hook for the DB-backed onboarding checklist.
 *          Wraps `usePageData('/v1/admin/setup')` and exposes typed selectors
 *          consumed by:
 *            - DashboardPage (banner mode SetupChecklist + redirect logic)
 *            - OnboardingPage (wizard mode)
 *            - per-page EmptyState nudges (e.g. "you need to install the SDK
 *              before reports show up here")
 */

import { useMemo } from 'react'
import type { ZodType, ZodTypeDef } from 'zod'
import { usePageData } from './usePageData'
import { SetupResponseSchema } from './apiSchemas'

export type SetupStepId =
  | 'project_created'
  | 'api_key_generated'
  | 'sdk_installed'
  | 'first_report_received'
  | 'github_connected'
  | 'sentry_connected'
  | 'byok_anthropic'
  | 'codebase_indexed'
  | 'autofix_enabled'
  | 'first_fix_dispatched'
  | 'slack_connected'
  | 'first_qa_story_passing'

/** Type-safe const enum for step IDs — eliminates string literals at call sites.
 *  Usage: `setup.isStepIncomplete(SETUP_STEPS.sdkInstalled)` */
export const SETUP_STEPS = {
  projectCreated: 'project_created',
  apiKeyGenerated: 'api_key_generated',
  sdkInstalled: 'sdk_installed',
  firstReportReceived: 'first_report_received',
  githubConnected: 'github_connected',
  sentryConnected: 'sentry_connected',
  byokAnthropic: 'byok_anthropic',
  codebaseIndexed: 'codebase_indexed',
  autofixEnabled: 'autofix_enabled',
  firstFixDispatched: 'first_fix_dispatched',
  slackConnected: 'slack_connected',
  firstQaStoryPassing: 'first_qa_story_passing',
} as const satisfies Record<string, SetupStepId>

/**
 * Optional diagnostic the backend emits alongside the `sdk_installed` step.
 * Sourced from `project_api_keys.last_seen_*` on every authed SDK request
 * (see packages/server/supabase/functions/_shared/auth.ts). Surfaced on
 * the checklist row so operators can confirm which backend their SDK is
 * actually reaching — the canonical fix for "checklist says SDK not
 * installed but my app is clearly running the widget" cross-env mishaps.
 */
export interface SetupStepDiagnostic {
  last_sdk_seen_at: string | null
  last_sdk_origin: string | null
  last_sdk_user_agent: string | null
  last_sdk_endpoint_host: string | null
}

export interface SetupStep {
  id: SetupStepId
  label: string
  description: string
  complete: boolean
  required: boolean
  cta_to: string
  cta_label: string
  diagnostic?: SetupStepDiagnostic
}

export interface SetupProject {
  project_id: string
  project_name: string
  project_slug: string
  created_at: string
  steps: SetupStep[]
  required_total: number
  required_complete: number
  total: number
  complete: number
  done: boolean
  report_count: number
  fix_count: number
  /** Subset of `fix_count` that have a non-null `merged_at` — i.e. the
   *  PR landed in the upstream repo. Drives the Check-stage transition
   *  from 'active' (PR open, awaiting merge) to 'done' (loop closed)
   *  on the dashboard's first-run PDCA card. */
  merged_fix_count: number
  /** Number of source files currently indexed in the codebase RAG index.
   *  0 means the project hasn't run `mushi index` yet — used by ExplorePage
   *  to show the "not indexed" empty state. */
  indexed_file_count?: number
}

export interface SetupResponse {
  has_any_project: boolean
  projects: SetupProject[]
  /** Host this admin is reading from; compared to per-step diagnostics. */
  admin_endpoint_host?: string | null
}

interface SetupSelectors {
  /** Aggregated counts across the active project (for the banner). */
  required_total: number
  required_complete: number
  total: number
  complete: number
  done: boolean
}

export interface UseSetupStatusResult {
  /** Raw payload (null while loading). */
  data: SetupResponse | null
  loading: boolean
  error: string | null
  reload: () => void
  /** True when the current user owns at least one project. */
  hasAnyProject: boolean
  /** The active project (first one for now; will become URL-driven in ). */
  activeProject: SetupProject | null
  /** Convenience selectors keyed off the active project. */
  selectors: SetupSelectors
  /** Quick check: is a particular step incomplete on the active project? */
  isStepIncomplete: (id: SetupStepId) => boolean
  /** Find a single step's metadata on the active project. */
  getStep: (id: SetupStepId) => SetupStep | null
}

const EMPTY_SELECTORS: SetupSelectors = {
  required_total: 0,
  required_complete: 0,
  total: 0,
  complete: 0,
  done: false,
}

export function useSetupStatus(activeProjectId?: string | null): UseSetupStatusResult {
  // FE-API-1: Zod-validate the response. Setup drives the onboarding gate,
  // the banner, and every per-page "finish setup first" nudge — silent
  // drift here sends users into a broken empty state with no diagnostic.
  // SetupStepIdSchema is z.string() (not z.enum) so new backend steps never
  // break Zod validation. The cast below bridges the Zod-inferred type (where
  // step.id is `string`) back to the SetupResponse interface (where step.id is
  // the narrower `SetupStepId` union). Runtime shape is unchanged.
  const { data, loading, error, reload } = usePageData<SetupResponse>('/v1/admin/setup', {
    schema: SetupResponseSchema as unknown as ZodType<SetupResponse, ZodTypeDef, SetupResponse>,
    scope: 'enumeration',
  })

  return useMemo(() => {
    const projects = data?.projects ?? []
    const explicit = activeProjectId
      ? projects.find(p => p.project_id === activeProjectId) ?? null
      : null
    const activeProject = explicit ?? projects[0] ?? null

    const selectors: SetupSelectors = activeProject
      ? {
          required_total: activeProject.required_total,
          required_complete: activeProject.required_complete,
          total: activeProject.total,
          complete: activeProject.complete,
          done: activeProject.done,
        }
      : EMPTY_SELECTORS

    const stepMap = new Map(activeProject?.steps.map(s => [s.id, s] as const) ?? [])

    return {
      data,
      loading,
      error,
      reload,
      hasAnyProject: Boolean(data?.has_any_project),
      activeProject,
      selectors,
      isStepIncomplete: (id: SetupStepId) => {
        if (!activeProject) return true
        const step = stepMap.get(id)
        return !step?.complete
      },
      getStep: (id: SetupStepId) => stepMap.get(id) ?? null,
    }
  }, [data, loading, error, reload, activeProjectId])
}
